import { z } from "zod";
import { patientApiRequest, ApiAuth, ApiError } from "../../../lib/api";

export const Hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;
export const Ymd = /^\d{4}-\d{2}-\d{2}$/;

export const ActivityTypeSchema = z.enum(["exercise", "nap", "caffeine", "alchohol", "drugs"]);
export const DayTypeSchema = z.enum(["work", "day-off", "sick-day", "vacation", "other"]);
export type ActivityType = z.infer<typeof ActivityTypeSchema>;
export type DayType = z.infer<typeof DayTypeSchema>;

const HhmmSchema = z.string().regex(Hhmm);
const YmdSchema = z.string().regex(Ymd);

// Types and formats only, no numeric upper bounds: a night converted from the
// legacy format can carry a value the save range refuses, and a failed parse
// would blank the page. The server validates every save.
export const NightInputSchema = z.object({
  day: YmdSchema,
  inBed: HhmmSchema,
  sleepLatencyMinutes: z.number().int(),
  wake: HhmmSchema,
  outOfBedAfterMinutes: z.number().int(),
  awakeningCount: z.number().int(),
  awakeningMinutes: z.number().int(),
  awakeningTimes: z.array(HhmmSchema),
  dayType: DayTypeSchema,
  activities: z.array(z.object({ type: ActivityTypeSchema, start: HhmmSchema, end: HhmmSchema })),
  notes: z.string(),
});
export type NightInput = z.infer<typeof NightInputSchema>;

// What the server sends back. A night converted from the legacy format can
// carry null clocks and minutes, and an unanswered schedule copy carries null
// awakening values, so every one of those is nullable here.
export const NightReadSchema = z.object({
  day: YmdSchema,
  inBed: HhmmSchema.nullable(),
  sleepLatencyMinutes: z.number().int().nullable(),
  wake: HhmmSchema.nullable(),
  outOfBedAfterMinutes: z.number().int().nullable(),
  awakeningCount: z.number().int().nullable(),
  awakeningMinutes: z.number().int().nullable(),
  awakeningTimes: z.array(HhmmSchema),
  dayType: DayTypeSchema,
  activities: z.array(z.object({ type: z.string(), start: HhmmSchema.nullable(), end: HhmmSchema.nullable() })),
  notes: z.string(),
});
export type NightRead = z.infer<typeof NightReadSchema>;

export const MetricsSchema = z.object({
  asleepMinutes: z.number().int().nullable(),
  inBedMinutes: z.number().int().nullable(),
  efficiencyPct: z.number().int().nullable(),
});
export type Metrics = z.infer<typeof MetricsSchema>;

export const NightViewSchema = z.object({
  day: YmdSchema,
  saved: z.boolean(),
  schemaVersion: z.union([z.literal(1), z.literal(2)]),
  input: NightReadSchema,
  metrics: MetricsSchema,
});
export type NightView = z.infer<typeof NightViewSchema>;

// lastNight is told on the clinic's clock (D19): the page opens on it, never on the device's date.
export const WeekResponseSchema = z.object({
  success: z.literal(true),
  from: YmdSchema,
  to: YmdSchema,
  lastNight: YmdSchema,
  timeZone: z.string(),
  days: z.array(YmdSchema),
});
export type WeekResponse = z.infer<typeof WeekResponseSchema>;

export const GetResponseSchema = z.object({
  success: z.literal(true),
  night: NightViewSchema.nullable(),
  prefill: NightReadSchema.nullable(),
});
export type GetResponse = z.infer<typeof GetResponseSchema>;

export const SaveResponseSchema = z.object({ success: z.literal(true), night: NightViewSchema });
export type SaveResponse = z.infer<typeof SaveResponseSchema>;

export const sleepDiaryApi = {
  /** With no range, the server answers the week that ends on last night. */
  async week(auth: ApiAuth, range?: { from: string; to: string }): Promise<WeekResponse> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: "/patient-app/sleep-diary/week",
      data: range ?? {},
    });
    return WeekResponseSchema.parse(data);
  },

  async get(auth: ApiAuth, day: string): Promise<GetResponse> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: "/patient-app/sleep-diary/get",
      data: { day },
    });
    return GetResponseSchema.parse(data);
  },

  async save(auth: ApiAuth, input: NightInput): Promise<SaveResponse> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: "/patient-app/sleep-diary/save",
      data: input,
    });
    return SaveResponseSchema.parse(data);
  },
};

/** A 400 INVALID_SLEEP_NIGHT message is "<field>: <reason>"; anything else is not a refusal of the night. */
export function readNightRefusal(error: unknown): { field: string; reason: string } | null {
  if (!(error instanceof ApiError) || error.status !== 400 || error.code !== "INVALID_SLEEP_NIGHT") return null;
  const at = error.message.indexOf(": ");
  if (at < 0) return null;
  return { field: error.message.slice(0, at), reason: error.message.slice(at + 2) };
}
